import {Mapper} from '@s-ui/domain'

export default class FromPageNameToTechnicalDetailsBreadcrumbVOMapper extends Mapper {
  constructor({config, valueObject}) {
    super()

    this._config = config
    this._valueObject = valueObject
  }

  _normalizeLabelToUpperCase = label =>
    label
      .split(/-|_|\s/)
      .join(' ')
      .toUpperCase()

  _normalizeMakeUrl = ({make}) => {
    let {TECHNICAL_DETAILS_MAKE_PATH_PATTERN: path} = this._config.get('page')
    path = path.replace(':make', make)
    return path
  }

  _normalizeModelUrl = ({make, model}) => {
    let {TECHNICAL_DETAILS_MODEL_PATH_PATTERN: path} = this._config.get('page')
    path = path.replace(':make', make)
    path = path.replace(':model', model)
    return path
  }

  _getHomeSegment = () => {
    const {TECHNICAL_DETAILS_PATH_PATTERN: url} = this._config.get('page')
    return {label: 'BREADCRUMB_TECHNICAL_DETAILS_HOME_LABEL', url}
  }

  /**
   *  Get breadcrumbs for specific pages
   */

  _getTechnicalDetailsDefaultBreadcrumbs = () => {
    const homeSegment = this._getHomeSegment()
    const sectionSegment = {label: 'BREADCRUMB_TECHNICAL_DETAILS_SECTION_LABEL'}

    return [homeSegment, sectionSegment]
  }

  _getTechnicalDetailsComparatorBreadcrumbs = () => {
    const homeSegment = this._getHomeSegment()
    const comparatorSegment = {
      label: 'BREADCRUMB_TECHNICAL_DETAILS_COMPARATOR_LABEL_COCHES'
    }

    return [homeSegment, comparatorSegment]
  }

  _getTechnicalDetailsMakeBreadcrumbs = ({make}) => {
    if (!make) return this._getTechnicalDetailsDefaultBreadcrumbs()

    const homeSegment = this._getHomeSegment()
    const makeSegment = {label: this._normalizeLabelToUpperCase(make)}

    return [homeSegment, makeSegment]
  }

  _getTechnicalDetailsModelBreadcrumbs = ({make, model}) => {
    if (!make || !model) return this._getTechnicalDetailsDefaultBreadcrumbs()

    const homeSegment = this._getHomeSegment()
    const makeSegment = {
      label: this._normalizeLabelToUpperCase(make),
      url: this._normalizeMakeUrl({make})
    }
    const modelSegment = {label: this._normalizeLabelToUpperCase(model)}

    return [homeSegment, makeSegment, modelSegment]
  }

  _getTechnicalDetailsBodyTypeBreadcrumbs = ({make, model, bodyType, doors}) => {
    if (!make || !model || !bodyType || !doors) return this._getTechnicalDetailsDefaultBreadcrumbs()

    const homeSegment = this._getHomeSegment()
    const makeSegment = {
      label: this._normalizeLabelToUpperCase(make),
      url: this._normalizeMakeUrl({make})
    }
    const modelSegment = {
      label: this._normalizeLabelToUpperCase(model),
      url: this._normalizeModelUrl({make, model})
    }
    const bodyTypeSegment = {
      label: 'BREADCRUMB_TECHNICAL_DETAILS_BODY_TYPE_LABEL',
      params: {
        model: this._normalizeLabelToUpperCase(model),
        bodyType,
        doors
      }
    }

    return [homeSegment, makeSegment, modelSegment, bodyTypeSegment]
  }

  _getTechnicalDetailsVersionBreadcrumbs = ({make, model, version}) => {
    const {HAS_MODEL_PAGE} = this._config.get('technicalDetails')
    if (!make || !model) return this._getTechnicalDetailsDefaultBreadcrumbs()

    const homeSegment = this._getHomeSegment()
    const makeSegment = {
      label: this._normalizeLabelToUpperCase(make),
      url: this._normalizeMakeUrl({make})
    }
    const modelSegment = {
      label: this._normalizeLabelToUpperCase(model),
      ...(HAS_MODEL_PAGE && {url: this._normalizeModelUrl({make, model})})
    }
    const versionSegment = {label: version}

    // Motos has a special case for Technical Details / Vehicle page
    // where vehicles can have no version. Breadcrumbs should be working anyway though
    const segments = [homeSegment, makeSegment, modelSegment]
    if (version) segments.push(versionSegment)

    return segments
  }

  _getTechnicalDetailsSearchBreadcrumbs = () => {
    const homeSegment = this._getHomeSegment()
    const searchSegment = {label: 'BREADCRUMB_TECHNICAL_DETAILS_SEARCH_LABEL'}

    return [homeSegment, searchSegment]
  }

  map = ({pageName, section: make, model, bodyType, doors, version}) => {
    const {
      TECHNICAL_DETAILS_HOME_PAGE_NAME,
      TECHNICAL_DETAILS_COMPARATOR_PAGE_NAME,
      TECHNICAL_DETAILS_COMPARATOR_RESULTS_PAGE_NAME,
      TECHNICAL_DETAILS_MAKE_PAGE_NAME,
      TECHNICAL_DETAILS_MODEL_PAGE_NAME,
      TECHNICAL_DETAILS_BODY_TYPE_PAGE_NAME,
      TECHNICAL_DETAILS_VERSION_PAGE_NAME,
      TECHNICAL_DETAILS_VEHICLE_PAGE_NAME,
      TECHNICAL_DETAILS_VEHICLE_WITHOUT_VERSION_PAGE_NAME,
      TECHNICAL_DETAILS_SEARCH_PAGE_NAME,
      TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME
    } = this._config.get('page')

    let breadcrumbs

    switch (pageName) {
      case TECHNICAL_DETAILS_HOME_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsDefaultBreadcrumbs()
        break
      case TECHNICAL_DETAILS_COMPARATOR_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsComparatorBreadcrumbs()
        break
      case TECHNICAL_DETAILS_COMPARATOR_RESULTS_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsComparatorBreadcrumbs()
        break
      case TECHNICAL_DETAILS_MAKE_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsMakeBreadcrumbs({make})
        break
      case TECHNICAL_DETAILS_MODEL_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsModelBreadcrumbs({make, model})
        break
      case TECHNICAL_DETAILS_BODY_TYPE_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsBodyTypeBreadcrumbs({
          make,
          model,
          bodyType,
          doors
        })
        break
      case TECHNICAL_DETAILS_VERSION_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsVersionBreadcrumbs({
          make,
          model,
          version
        })
        break
      case TECHNICAL_DETAILS_VEHICLE_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsVersionBreadcrumbs({
          make,
          model,
          version
        })
        break
      case TECHNICAL_DETAILS_VEHICLE_WITHOUT_VERSION_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsVersionBreadcrumbs({
          make,
          model
        })
        break
      case TECHNICAL_DETAILS_SEARCH_PAGE_NAME:
      case TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME:
        breadcrumbs = this._getTechnicalDetailsSearchBreadcrumbs()
        break
      default:
        breadcrumbs = this._getTechnicalDetailsDefaultBreadcrumbs()
        break
    }

    return this._valueObject({breadcrumbs})
  }
}
